import type {
  Category,
  EventId,
  EventSubtask,
  HabitId,
  RecurrenceRule,
  SubtaskId,
  TaskId,
} from "../../shared/types";
import { apiFetch, hasUserId } from "./client";
import { enqueue, type QueuedMutation } from "./offline-queue";

/**
 * Fire-and-forget sync to the database with retry and pending tracking.
 * Each function mirrors a local store mutation and pushes it to the server.
 * Failed creates are tracked so resetFromServer can preserve them.
 */

const PENDING_EVENTS_KEY = "palnia_pending_events";
const PENDING_TASKS_KEY = "palnia_pending_tasks";
const PENDING_HABITS_KEY = "palnia_pending_habits";

function getPendingIds(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function savePendingIds(key: string, ids: Set<string>) {
  if (ids.size === 0) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, JSON.stringify([...ids]));
  }
}

function markPending(key: string, id: string) {
  const ids = getPendingIds(key);
  ids.add(id);
  savePendingIds(key, ids);
}

function unmarkPending(key: string, id: string) {
  const ids = getPendingIds(key);
  ids.delete(id);
  savePendingIds(key, ids);
}

export function getPendingEventIds(): Set<string> {
  return getPendingIds(PENDING_EVENTS_KEY);
}

export function getPendingTaskIds(): Set<string> {
  return getPendingIds(PENDING_TASKS_KEY);
}

export function getPendingHabitIds(): Set<string> {
  return getPendingIds(PENDING_HABITS_KEY);
}

function sync(
  entity: QueuedMutation["entity"],
  entityId: string,
  type: QueuedMutation["type"],
  path: string,
  method: string,
  body?: string,
): void {
  if (!hasUserId()) return;
  const doRequest = () => apiFetch(path, { method, ...(body ? { body } : {}) });

  doRequest().catch((err) => {
    console.warn("[db-sync] Sync failed, retrying in 3s:", err);
    setTimeout(() => {
      doRequest().catch((retryErr) => {
        console.error("[db-sync] Retry failed — queuing for later:", retryErr);
        enqueue({ entity, entityId, type, path, method, body });
      });
    }, 3000);
  });
}

// Debounce pour les updates — fusionne les modifications rapides (notes, titre, etc.)
const updateTimers = new Map<string, ReturnType<typeof setTimeout>>();
const pendingUpdates = new Map<string, Record<string, unknown>>();

function syncDebounced(
  entity: QueuedMutation["entity"],
  entityId: string,
  path: string,
  updates: Record<string, unknown>,
): void {
  if (!hasUserId()) return;
  const key = `${entity}:${entityId}`;

  const existing = pendingUpdates.get(key) ?? {};
  pendingUpdates.set(key, { ...existing, ...updates });

  const prev = updateTimers.get(key);
  if (prev) clearTimeout(prev);

  updateTimers.set(key, setTimeout(() => {
    const merged = pendingUpdates.get(key);
    pendingUpdates.delete(key);
    updateTimers.delete(key);
    if (merged) {
      sync(entity, entityId, "update", path, "PATCH", JSON.stringify(merged));
    }
  }, 500));
}

/** Sync with pending tracking — marks ID as pending until confirmed. */
function syncTracked(
  pendingKey: string,
  id: string,
  entity: QueuedMutation["entity"],
  path: string,
  method: string,
  body: string,
): void {
  if (!hasUserId()) return;
  markPending(pendingKey, id);
  const doRequest = () => apiFetch(path, { method, body });

  doRequest()
    .then(() => unmarkPending(pendingKey, id))
    .catch((err) => {
      console.warn("[db-sync] Sync failed for", id, "— retrying in 3s:", err?.message ?? err);
      setTimeout(() => {
        doRequest()
          .then(() => unmarkPending(pendingKey, id))
          .catch((retryErr) => {
            console.error("[db-sync] Retry failed for", id, "— queuing for later:", retryErr?.message ?? retryErr);
            enqueue({ entity, entityId: id, type: "create", path, method, body });
          });
      }, 3000);
    });
}

// ──────────────────────── Events ────────────────────────

export function syncCreateEvent(event: {
  id: string;
  title: string;
  category: Category;
  date: string;
  startTime: string;
  endTime: string;
  description?: string;
  notes?: string;
  tags?: readonly string[];
  recurrence?: RecurrenceRule | null;
  reminderMinutes?: number | null;
  allDay?: boolean;
  detachedFromId?: string;
  excludedDates?: readonly string[];
  eventSubtasks?: readonly EventSubtask[];
}): void {
  const body = JSON.stringify({
    id: event.id,
    title: event.title,
    category: event.category,
    date: event.date,
    startTime: event.startTime,
    endTime: event.endTime,
    description: event.description ?? "",
    notes: event.notes ?? "",
    tags: event.tags ? [...event.tags] : [],
    recurrence: event.recurrence ?? null,
    reminderMinutes: event.reminderMinutes ?? null,
    allDay: event.allDay ?? false,
    detachedFromId: event.detachedFromId ?? null,
    excludedDates: event.excludedDates ? [...event.excludedDates] : [],
    eventSubtasks: event.eventSubtasks ? [...event.eventSubtasks] : null,
  });
  syncTracked(PENDING_EVENTS_KEY, event.id, "event", "/events", "POST", body);
}

export function syncUpdateEvent(
  id: EventId,
  updates: Record<string, unknown>,
): void {
  syncDebounced("event", id, `/events/${id}`, updates);
}

export function syncDeleteEvent(id: EventId): void {
  sync("event", id, "delete", `/events/${id}`, "DELETE");
}

export function syncImportAllEvents(events: readonly unknown[]): void {
  sync("event", "batch", "create", "/events/import", "POST", JSON.stringify({ events }));
}

// ──────────────────────── Tasks ────────────────────────

export function syncCreateTask(task: {
  id: string;
  title: string;
  category: Category;
  dueDate?: string | null;
  priority?: string;
  notes?: string;
  tags?: readonly string[];
  eventId?: string | null;
  reminderMinutes?: number | null;
}): void {
  const body = JSON.stringify({
    id: task.id,
    title: task.title,
    category: task.category,
    dueDate: task.dueDate ?? null,
    priority: task.priority ?? "normal",
    notes: task.notes ?? "",
    tags: task.tags ? [...task.tags] : [],
    eventId: task.eventId ?? null,
    reminderMinutes: task.reminderMinutes ?? null,
  });
  syncTracked(PENDING_TASKS_KEY, task.id, "task", "/tasks", "POST", body);
}

export function syncUpdateTask(
  id: TaskId,
  updates: Record<string, unknown>,
): void {
  syncDebounced("task", id, `/tasks/${id}`, updates);
}

export function syncDeleteTask(id: TaskId): void {
  sync("task", id, "delete", `/tasks/${id}`, "DELETE");
}

export function syncCycleTaskStatus(id: TaskId): void {
  sync("task", id, "update", `/tasks/${id}/cycle-status`, "POST");
}

export function syncAddSubtask(taskId: TaskId, subtask: { id: string; title: string }): void {
  sync(
    "subtask",
    subtask.id,
    "create",
    `/tasks/${taskId}/subtasks`,
    "POST",
    JSON.stringify({ id: subtask.id, title: subtask.title }),
  );
}

export function syncToggleSubtask(taskId: TaskId, subtaskId: SubtaskId): void {
  sync("subtask", subtaskId, "update", `/tasks/${taskId}/subtasks/${subtaskId}`, "PATCH");
}

export function syncDeleteSubtask(taskId: TaskId, subtaskId: SubtaskId): void {
  sync("subtask", subtaskId, "delete", `/tasks/${taskId}/subtasks/${subtaskId}`, "DELETE");
}

export function syncReorderTask(taskId: TaskId, newIndex: number): void {
  sync("task", taskId, "update", "/tasks/reorder", "POST", JSON.stringify({ taskId, newIndex }));
}

// ──────────────────────── Habits ────────────────────────

export function syncCreateHabit(habit: {
  id: string;
  title: string;
  category: Category;
  frequency: "daily" | "weekly";
}): void {
  const body = JSON.stringify({
    id: habit.id,
    title: habit.title,
    category: habit.category,
    frequency: habit.frequency,
  });
  syncTracked(PENDING_HABITS_KEY, habit.id, "habit", "/habits", "POST", body);
}

export function syncUpdateHabit(
  id: HabitId,
  updates: Record<string, unknown>,
): void {
  syncDebounced("habit", id, `/habits/${id}`, updates);
}

export function syncDeleteHabit(id: HabitId): void {
  sync("habit", id, "delete", `/habits/${id}`, "DELETE");
}

export function syncToggleHabitDate(id: HabitId, date: string): void {
  sync("habit", id, "update", `/habits/${id}/toggle`, "POST", JSON.stringify({ date }));
}

// ──────────────────────── Categories ────────────────────────

export function syncCreateCategory(
  category: {
    name: string;
    label: string;
    colorId: string;
    goalHours?: number | null;
    showInWorkHours?: boolean;
  },
  onCreated?: (serverId: string) => void,
): void {
  const makeRequest = () =>
    apiFetch<{ id: string }>("/categories", {
      method: "POST",
      body: JSON.stringify(category),
    }).then((result) => {
      if (result?.id && onCreated) onCreated(result.id);
    });
  if (!hasUserId()) return;
  makeRequest().catch((err) => {
    console.warn("[db-sync] Category sync failed, retrying in 3s:", err);
    setTimeout(() => {
      makeRequest().catch((retryErr) => {
        console.error("[db-sync] Category retry failed:", retryErr);
      });
    }, 3000);
  });
}

export function syncUpdateCategory(
  id: string,
  updates: Partial<{ label: string; colorId: string; goalHours: number | null; showInWorkHours: boolean; hidden: boolean }>,
): void {
  syncDebounced("category", id, `/categories/${id}`, updates);
}

export function syncDeleteCategory(id: string): void {
  sync("category", id, "delete", `/categories/${id}`, "DELETE");
}

export function syncImportCategories(categories: readonly {
  name: string;
  label: string;
  colorId: string;
  goalHours?: number | null;
  showInWorkHours?: boolean;
  hidden?: boolean;
}[]): void {
  sync("category", "batch", "create", "/categories/import", "POST", JSON.stringify({ categories }));
}

